// Brief 85 §2 — ClientModal, moved out of App.tsx's renderModals switch.
//
// MOVED, NOT REWRITTEN. The body below is byte-identical to the `case "cl":`
// clause it left —
//   sha256 ff43dfc8685b040eaea69b65085cc9c4a42048753dad62481f067a44b54a2d7a
// — including its original indentation, which is why it sits deeper than this
// file's own wrapper. Only the imports, the Props type, the signature and the
// `return (` / `);` are new.
// Do not reformat: the indentation IS the proof.
//
// 168A §1b/§1c ARE THE FIRST EDITS TO THAT BODY, and the sha above therefore
// describes the MOVE and no longer the file. What changed and nothing else: the
// title wears the prospect badge while `form.isProspect` is set, and a tagged
// row that already exists offers ONE action beside it. No field, no status
// control (141 §2.4 — the client modal owns no status; ruling 1 of 17 Aug — the
// tag enters no taxonomy), and the save path is untouched.
//
// Props are the UNION of the names the body actually read in App scope, taken
// from a TypeScript-checker walk rather than chosen — a passed-but-unread prop
// would mean the union rule absorbed something it should have refused.
// The vocabulary props are typed by inference off makeModalFields' return, so
// this file adds nothing to the `any` ratchet.

/* 168A close — `import React` is `react/react-in-jsx-scope`'s OWN remedy, and
   the eslint ratchet counts that rule PER JSX ELEMENT: §1b and §1c added
   elements to a file that had none of them baselined, taking it 64 → 85. The
   import removes all 35 of that rule's warnings at once rather than re-recording
   a higher baseline (SESSION_RULES: "a ratchet is not re-recorded to make it
   pass"). `clientNameCell.tsx` carries the same import for the same reason. */
import React from "react";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import FieldSelect from "../FieldSelect";
// 182 B.2 — the referrer picker: the tree's searchable single-pick over a list.
import Combobox from "../Combobox";
import { ModalLayout } from "../SharedUI";
import CustomFieldsBlock from "../CustomFieldsBlock"; // 189 §B — the firm's own fields, after the built-in ones
import AmountField from "../AmountField"; // 207C §2 — the one amount field (the quantum)
import { convertClient } from "../../api";
// 182 B.2 — the two CRM kinds, read here (cached per kind) rather than threaded from App.
import { useTaxonomy, useTaxonomyList } from "../../hooks/useTaxonomy";
import { toDisplayList } from "../../utils/taxonomyMap"; // 207B §6 — the country list in the client's language
import { FS } from "../../lib/tokens";
import { SK, pname } from "../../utils";
import type { TaxList, TFn } from "../../lib/taxonomyTypes";
import type { FieldSelectOption } from "../FieldSelect";
import type { makeModalFields } from "../modalFields";

type Vocab = ReturnType<typeof makeModalFields>;
type ModalForm = { contractNumber?: string | null; country?: string | null; customFields?: Record<string, unknown> | null; dateOfBirth?: string | null; formerCaseRef?: string | null; id?: string | null; idDocumentExpiresAt?: string | null; idDocumentIssuedAt?: string | null; isProspect?: boolean | null; lang?: string | null; lostReason?: string | null; marketingConsent?: boolean | null; marketingConsentAt?: string | null; marketingConsentSource?: string | null; quantum?: string | null; referrerClientId?: number | null; source?: string | null; type?: string | null };
/* 182 B.2 — what the referrer picker reads off a client row. */
interface ReferrerLike { id?: number | null; email?: string | null }
/* 189 A.1 — the three-valued consent column and the select's three option
   VALUES, both directions. Functions, not a word table: a nested ternary is
   what one lint refuses and a module-level object of words is what another
   reads as prose reaching a human (these are option values; the words the
   reader sees are the `consent.state.*` keys). */
function consentWord(v: boolean | null | undefined): string {
  if (v === true) return "given";
  if (v === false) return "refused";
  return "";
}
function consentBool(w: string): boolean | null {
  if (w === "given") return true;
  if (w === "refused") return false;
  return null;
}

type Props = {
  F: Vocab["F"];
  NA: Vocab["NA"];
  PF: Vocab["PF"];
  S: Vocab["S"];
  T: Vocab["T"];
  _fl: Vocab["_fl"];
  _mt: Vocab["_mt"];
  clientTypesList: readonly FieldSelectOption[];
  clients: readonly unknown[];
  cm: () => void;
  countriesList: readonly FieldSelectOption[];
  countryEntries: TaxList;
  form: ModalForm;
  saveEntityGuarded: (k: string, rows: readonly unknown[], set: (v: readonly unknown[]) => void, f: unknown, ext?: Record<string, unknown>) => void;
  /* 168A §1c — WIDENED, NOT FORKED (SESSION_RULES rule 2): App's `setClients`
     is a React setter and has always accepted the updater form; this type named
     only the half `saveEntityGuarded` passes. The convert merges one row into
     whatever the list currently holds, which is the updater. */
  setClients: (v: readonly unknown[] | ((prev: unknown) => unknown)) => void;
  /* 168A §1c — the same four names `CaseModal` takes for the matter convert
     (`CaseModal.tsx:66,96,104,106`), so the two conversions are one shape. */
  confirmAndUndo: (o: { label?: unknown; title?: string; message?: string; confirmLabel?: string; onConfirm: () => void; undo?: { snapshot: unknown; restore: (snap: unknown) => void } | null; toastName?: unknown; windowMs?: number; onExpire?: () => void }) => void;
  setForm: (f: (p: ModalForm) => ModalForm) => void;
  toast: (msg: string, kind?: string) => void;
  t: TFn;
  updateField: (k: string, v: unknown) => void;
};

export default function ClientModal({
  F,
  NA,
  PF,
  S,
  T,
  _fl,
  _mt,
  clientTypesList,
  clients,
  cm,
  countriesList,
  countryEntries,
  confirmAndUndo,
  form,
  saveEntityGuarded,
  setClients,
  setForm,
  t,
  toast,
  updateField,
}: Props) {
  /* 182 B.2 — THE PROSPECT COLUMNS (B.1). Two FREE kinds through useTaxonomy
     (cached per kind; the display list is FieldSelect's own [code, label]
     shape, the entries feed its provisional-option check), and the referrer
     picked from the loaded client list minus this client. Typed at the seam:
     the hook is `any`-typed and this file may not rise. */
  const leadSources = useTaxonomyList("lead_source") as readonly FieldSelectOption[];
  const lostReasons = useTaxonomyList("lost_reason") as readonly FieldSelectOption[];
  const leadSourceEntries = useTaxonomy("lead_source").entries as TaxList;
  const lostReasonEntries = useTaxonomy("lost_reason").entries as TaxList;
  /* 189 A.1 — the consent source, the third CRM kind, read the same way. */
  const consentSources = useTaxonomyList("consent_source") as readonly FieldSelectOption[];
  const consentSourceEntries = useTaxonomy("consent_source").entries as TaxList;
  /* 207B §6 [RULED] — the country list named in the language chosen in this
     form, following it live; with none chosen, the reader's (the list App
     hands in, as before). German has no names in the vocabulary: its own
     fallback (the English) answers. */
  const formLang = typeof form.lang === "string" && form.lang ? form.lang : undefined;
  const countriesIn = formLang
    ? (toDisplayList(countryEntries, "country", formLang) as readonly FieldSelectOption[])
    : countriesList;
  /* 189 A.1 — the select speaks three words for a three-valued column
     (null / true / false); the two directions of that mapping live here and
     nowhere else. Changing the answer stamps TODAY into the date when the date
     is empty — "recorded here, with today's date and the reason" — and never
     overwrites a date somebody typed. */
  const consentValue = consentWord(form.marketingConsent);
  const setConsent = (v: string) => {
    updateField("marketingConsent", consentBool(v));
    if (v && !form.marketingConsentAt) updateField("marketingConsentAt", new Date().toISOString().slice(0, 10));
  };
  const selfId = form.id ?? "";
  const referrerOptions = (clients as readonly ReferrerLike[])
    .filter((c) => c.id != null && String(c.id) !== selfId)
    .map((c) => ({ value: String(c.id), label: pname(c), sub: c.email ?? "" }));
  /* 168A §1c — ADVISOR-PROPOSED, LAURENT DECIDES AT THE GLANCE.
     "Convert to client", for a tagged person who already exists.

     WHY IT IS OWED. The three ways off the tag are all about something else:
     a prospect CASE converting (140 §2), a collective PROMOTION (168 §1e), and
     the verb those two call. A person who never joins an action and never rides
     a case conversion has no way off it at all — and 168A §1a is what makes
     that person creatable in the first place. This is the way off.

     ONE ACT, NO UNDO, and that is a fact about the server rather than a choice:
     `POST /clients/:id/convert` (`clients.js:567`) is the only writer that
     clears `Client.isProspect` and there is no inverse — `isProspect` is
     create-only on the body and is not in the PUT's `ALLOWED`. `convertCase`
     has `convertCaseUndo`; this has nothing to call, so the dialog is a PROCEED
     dialog (`message` + `confirmLabel`, `undo: null`) exactly as
     `useConfirmAndUndo` documents that combination.

     Its own title and its own verb (166 R18): the person's name, and a sentence
     that names what the conversion does.

     The answer merges the way 168 §1e merges it — the route returns the whole
     client, so one `setClients(prev => prev.map(…))` and no second fetch — and
     the FORM is merged too, because this modal is open on that row and the
     badge above reads `form.isProspect`. */
  const convertToClient = () => {
    const id = Number(form.id);
    if (!id || !form.isProspect) return;
    confirmAndUndo({
      title: pname(form) || String(id),
      message: t("client.convert.confirm", {
        fallback:
          "This person is marked as a prospect. Converting removes that mark. Continue?",
      }),
      confirmLabel: t("common.continue", { fallback: "Continue" }),
      onConfirm: () => {
        convertClient(id)
          .then((updated) => {
            setClients((prev: unknown) =>
              Array.isArray(prev)
                ? prev.map((c: { id?: number }) =>
                    c && c.id === id ? { ...c, ...updated } : c,
                  )
                : prev,
            );
            setForm((p: ModalForm) => ({ ...p, isProspect: false }));
            toast(
              t("client.convert.done", {
                name: pname(form),
                fallback: `${pname(form)} is now a client`,
              }),
              "success",
            );
          })
          .catch((e: Error) => toast(e.message, "error"));
      },
      undo: null,
    });
  };
  return (
          <ModalLayout
            recordId={form.id} // 204 §G — the person's internal id in the header
            title={
              /* 168A §1b — THE MODAL WEARS THE TAG, AND ONLY WEARS IT.
                 The pill is `clientNameCell`'s, which is `matterListCfg`'s:
                 same `.status-prospect` class, same seeded
                 `matters.prospect.badge`. DISPLAY ONLY — there is no field to
                 change it here, because the create route reads `isProspect` off
                 the body once (`clients.js:322`) and the edit PUT's `ALLOWED`
                 (`clients.js:27`) does not carry it at all, by design: an edit
                 re-sends the whole form snapshot and must never flip the tag.
                 So an edit of a tagged person leaves the tag exactly where it
                 was, and the badge says which row you are on. */
              form.isProspect ? (
                <>
                  {form.id ? _mt("Edit Client") : _mt("New Client")}{" "}
                  <span className="status status-prospect">
                    {t("matters.prospect.badge", { fallback: "Prospect" })}
                  </span>
                  {/* 168A §1c — only on a row that EXISTS: there is nothing to
                      convert before the create, and the create is where the tag
                      is set. */}
                  {form.id ? (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: "2px 7px", fontSize: FS.s10 }}
                        onClick={convertToClient}
                      >
                        {t("client.convert.action", {
                          fallback: "Convert to client",
                        })}
                      </button>
                    </>
                  ) : null}
                </>
              ) : (
                (form.id ? _mt("Edit Client") : _mt("New Client"))
              )
            }
            onClose={cm}
            onSave={() =>
              saveEntityGuarded(SK.cl, clients, setClients, form, {
                type: form.type || "individual",
                // 207B §6 — what the form shows is what is saved: an empty
                // language stays empty (a new client starts with « fr » in the
                // form); « fr » written in its place was a phantom.
                lang: form.lang || null,
                country: form.country || null,
              })
            }
          >
              <>
                {S("Type", "type", clientTypesList)}
                {(form.type || "individual") === "company" ? (
                  <>
                    {F("Company Name *", "companyName", "", 1)}
                    <div className="field-row">
                      {F("VAT", "vat")}
                      {F(
                        t("cdd.field.bce", { fallback: "Enterprise number" }),
                        "bceNumber",
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {PF}
                    {F("National Register Number", "nationalRegisterId")}
                    <div className="field-row">
                      <div className="field">
                        <label>
                          {_fl(
                            t("cdd.field.dob", { fallback: "Date of birth" }),
                          )}
                        </label>
                        <input
                          className="field-input"
                          type="date"
                          value={
                            form.dateOfBirth
                              ? String(form.dateOfBirth).slice(0, 10)
                              : ""
                          }
                          onChange={(e) =>
                            updateField("dateOfBirth", e.target.value || null)
                          }
                        />
                      </div>
                      {F(
                        t("cdd.field.pob", { fallback: "Place of birth" }),
                        "placeOfBirth",
                      )}
                    </div>
                    {F(
                      t("cdd.field.nationality", { fallback: "Nationality" }),
                      "nationality",
                    )}
                    <div className="field-row">
                      {F(
                        t("cdd.field.id_doc_type", {
                          fallback: "ID document type",
                        }),
                        "idDocumentType",
                        t("cdd.field.id_doc_type_ph", {
                          fallback: "passport / national_id",
                        }),
                      )}
                      {F(
                        t("cdd.field.id_doc_number", {
                          fallback: "ID document number",
                        }),
                        "idDocumentNumber",
                      )}
                    </div>
                    <div className="field-row">
                      <div className="field">
                        <label>
                          {_fl(
                            t("cdd.field.id_doc_issued_at", {
                              fallback: "ID issued",
                            }),
                          )}
                        </label>
                        <input
                          className="field-input"
                          type="date"
                          value={
                            form.idDocumentIssuedAt
                              ? String(form.idDocumentIssuedAt).slice(0, 10)
                              : ""
                          }
                          onChange={(e) =>
                            updateField("idDocumentIssuedAt", e.target.value || null)
                          }
                        />
                      </div>
                      <div className="field">
                        <label>
                          {_fl(
                            t("cdd.field.id_doc_expires_at", {
                              fallback: "ID expires",
                            }),
                          )}
                        </label>
                        <input
                          className="field-input"
                          type="date"
                          value={
                            form.idDocumentExpiresAt
                              ? String(form.idDocumentExpiresAt).slice(0, 10)
                              : ""
                          }
                          onChange={(e) =>
                            updateField("idDocumentExpiresAt", e.target.value || null)
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
                {NA({
                  pool: clients,
                  typeLabel: t("type.client", { fallback: "client" }),
                })}
                {/* Phase 23 — standalone contract identifier, above Quantum. 211 §16 [RULED 24] — the
                    client's own « Ref. contract » (kept for the verticals whose clients contract with
                    their customers): its label reads its language (_LBL_KEYS). A case's numbers are
                    the case's, edited in the case (§17). */}
                <div className="field">
                  <label>{_fl("Ref. contract")}</label>
                  <input
                    {...NO_AUTOFILL}
                    className={noAutofillClass("field-input")}
                    value={form.contractNumber || ""}
                    onChange={(e) => updateField("contractNumber", e.target.value)}
                    placeholder=""
                  />
                </div>
                {/* Phase 43 (item 4 / D17) — former-matter reference, ingested
                    from the archived-client import `case` column; editable here. */}
                <div className="field">
                  <label>{_fl("Former Case Ref")}</label>
                  <input
                    {...NO_AUTOFILL}
                    className={noAutofillClass("field-input")}
                    value={form.formerCaseRef || ""}
                    onChange={(e) => updateField("formerCaseRef", e.target.value)}
                    placeholder=""
                  />
                </div>
                <div className="field">
                  <label>{_fl("Quantum")}</label>
                  {/* 207C §2 [RULED, ruling 6] — the one amount field (the rate card's,
                      src/components/AmountField.tsx): shown « 92 159,50 », typed with a
                      decimal comma. It was `type="number"`, which took no « , »: « 92 159,5 »
                      typed was saved 921595 (measured, both engines). */}
                  <AmountField
                    value={form.quantum}
                    empty=""
                    inputMode="decimal"
                    onAmount={(n) => updateField("quantum", n)}
                    placeholder="0,00"
                  />
                </div>
                <div className="field-row">
                  {F("Email", "email")}
                  {F("Phone", "phone")}
                </div>
                {F("Street", "street")}
                <div className="field-row">
                  {F("Postal Code", "postalCode")}
                  {F("City", "city")}
                </div>
                <div className="field-row">
                  {/* 207B §6 [RULED] — the country, its list and its value, named
                      in the language chosen beside it, following it live; with
                      none chosen, the reader's. Searching still finds a country
                      by any language's name (`entries`). */}
                  <FieldSelect
                    label={_fl("Country")}
                    value={form.country}
                                onChange={(v) => updateField("country", v)}
                    options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...countriesIn]}
                    searchable
                    entries={countryEntries}
                    t={t}
                  />
                  {/* 154B §2 — the row was .field + <label> + select.field-input,
                      which is FieldSelect's own shape, and `fr` was the display
                      default rather than something the record held: it stays a
                      `fallback`, not a `value`. */}
                  {/* 207B §6 [RULED] — the language shows what is stored (207
                      §13's rule): the `fallback="fr"` it showed and did not hold
                      is gone; an empty language reads « — ». */}
                  <FieldSelect
                    label={_fl("Language")}
                    value={form.lang ?? ""}
                    onChange={(v) => updateField("lang", v)}
                    options={[
                      ["", "—"],
                      ["fr", t("lang.french", { fallback: "French" })],
                      ["nl", t("lang.dutch", { fallback: "Dutch" })],
                      ["en", t("lang.english", { fallback: "English" })],
                      ["de", t("lang.german", { fallback: "German" })],
                    ]}
                  />
                </div>
                {/* 189 A.1 — MARKETING CONSENT, in the contact-details block
                    (189 (3): the client record is the authority; the public
                    unsubscribe page never writes here). Editable by hand: a
                    client who telephones to say "stop sending me those" is
                    recorded with today's date and the reason. Three fields,
                    one row, after the other contact details and before 182's
                    relationship row. */}
                <div className="field-row">
                  <FieldSelect
                    label={t("field.marketing_consent", { fallback: "Marketing consent" })}
                    value={consentValue}
                    onChange={(v) => { setConsent(v); }}
                    options={[
                      ["", t("consent.state.unknown", { fallback: "Never asked" })],
                      ["given", t("consent.state.given", { fallback: "Given" })],
                      ["refused", t("consent.state.refused", { fallback: "Refused" })],
                    ]}
                  />
                  <div className="field">
                    <label>{t("field.marketing_consent_at", { fallback: "Consent recorded on" })}</label>
                    <input
                      className="field-input"
                      type="date"
                      value={form.marketingConsentAt ? form.marketingConsentAt.slice(0, 10) : ""}
                      onChange={(e) => { updateField("marketingConsentAt", e.target.value || null); }}
                    />
                  </div>
                  <FieldSelect
                    label={t("field.marketing_consent_source", { fallback: "Consent source" })}
                    value={form.marketingConsentSource}
                    onChange={(v) => {
                      updateField("marketingConsentSource", v || null);
                    }}
                    options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...consentSources]}
                    entries={consentSourceEntries}
                    t={t}
                  />
                </div>
                {/* 182 B.2 — where they came from, who sent them and, for a
                    prospect, why they were lost. `lostReason` renders for a
                    tagged row only and setting it does NOT flip the tag: a lost
                    prospect stays a prospect with a reason, which is what the
                    funnel counts. MEASURED: this modal holds no prospect SWITCH
                    to sit under — 168A §1b made the tag display-only here (the
                    create route reads it once) — so the row sits with the other
                    relationship fields, before Notes. A cleared select sends ""
                    and the route stores NULL (an empty string would count as
                    lost). */}
                <div className="field-row">
                  <FieldSelect
                    label={_fl("Source")}
                    value={form.source}
                    onChange={(v) => {
                      updateField("source", v || null);
                    }}
                    options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...leadSources]}
                    entries={leadSourceEntries}
                    t={t}
                  />
                  <div className="field">
                    <label>{_fl("Referred by")}</label>
                    <Combobox
                      value={form.referrerClientId ? String(form.referrerClientId) : ""}
                      onChange={(v: unknown) => {
                        updateField("referrerClientId", v ? Number(v) : null);
                      }}
                      options={referrerOptions}
                      placeholder={t("common.select_dots", { fallback: "Select..." })}
                    />
                  </div>
                  {form.isProspect ? (
                    <FieldSelect
                      label={_fl("Lost reason")}
                      value={form.lostReason}
                      onChange={(v) => {
                        updateField("lostReason", v || null);
                      }}
                      options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...lostReasons]}
                      entries={lostReasonEntries}
                      t={t}
                    />
                  ) : null}
                </div>
                {T("Notes", "notes")}
                {/* 189 §B — the firm's own fields, after the built-in ones, in
                    the definitions' order. */}
                <CustomFieldsBlock
                  entity="client"
                  values={form.customFields}
                  onChange={(v) => { updateField("customFields", v); }}
                />
              </>
          </ModalLayout>
  );
}
